import { useEffect, useState, useRef } from 'react'
import type { JSX } from 'react'
import type { FormEvent } from 'react'

import { Button, Dialog, Form, Input, Text } from '@buffer-mono/popcorn'

import styles from './ConfirmPasswordModal.module.css'

const ConfirmPasswordModal = ({
  open,
  onOpenChange,
  onConfirm,
  description,
  children,
  error,
  isLoading = false,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  onConfirm: (password: string) => void
  description: string | JSX.Element
  children: React.ReactNode
  error?: string
  isLoading?: boolean
}): JSX.Element => {
  const [password, setPassword] = useState('')
  const dialogContentRef = useRef<HTMLDivElement | null>(null)

  const handleSubmit = (e: FormEvent): void => {
    e.preventDefault()
    onConfirm(password)
  }

  // Clear password when modal closes
  useEffect(() => {
    if (!open) {
      setPassword('')
    }
  }, [open])

  // Clear password when there's an error to force user to re-enter
  useEffect(() => {
    if (error) {
      setPassword('')
    }
  }, [error])

  return (
    <>
      {children}
      <Dialog open={open} onOpenChange={onOpenChange} modal>
        <Dialog.Content
          ref={dialogContentRef}
          onPointerDownOutside={(e): void => {
            if (!dialogContentRef.current) return
            const contentRect = dialogContentRef.current.getBoundingClientRect()
            // Detect if click actually happened within the bounds of content.
            // This can happen if click was on an absolutely positioned element overlapping content,
            // such as the 1Password extension icon in the text input.
            // https://github.com/radix-ui/primitives/issues/1280#issuecomment-1319109163
            const actuallyClickedInside =
              e.detail.originalEvent.clientX > contentRect.left &&
              e.detail.originalEvent.clientX <
                contentRect.left + contentRect.width &&
              e.detail.originalEvent.clientY > contentRect.top &&
              e.detail.originalEvent.clientY <
                contentRect.top + contentRect.height
            if (actuallyClickedInside) {
              e.preventDefault()
            }
          }}
        >
          <Form onSubmit={handleSubmit} className={styles.form}>
            <Dialog.Header>
              <Dialog.Title>Authentication Required</Dialog.Title>
              <Dialog.Description>{description}</Dialog.Description>
            </Dialog.Header>
            <Dialog.Body>
              <Form.Field name="current-password">
                <Form.Label id="current-password">Password</Form.Label>
                <Form.Control>
                  <Input
                    type="password"
                    autoComplete="current-password"
                    placeholder="Password"
                    name="current-password"
                    value={password}
                    onChange={(e): void => setPassword(e.target.value)}
                    aria-invalid={error ? true : undefined}
                    aria-describedby={error ? 'password-error' : undefined}
                    required
                  />
                </Form.Control>
                {error && (
                  <Text color="critical" size="sm" id="password-error">
                    {error}
                  </Text>
                )}
              </Form.Field>
            </Dialog.Body>
            <Dialog.Footer>
              <Button
                variant="tertiary"
                type="button"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                type="submit"
                loading={isLoading}
                disabled={!password.trim()}
              >
                Submit
              </Button>
            </Dialog.Footer>
          </Form>
        </Dialog.Content>
      </Dialog>
    </>
  )
}

export default ConfirmPasswordModal
